import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { API } from "@/lib/api";
import {
  ShieldCheck, CheckCircle, XCircle, Certificate as CertIcon,
  PlugsConnected, MapPin, LockKey, FileText, ArrowSquareOut,
  Hash, CalendarBlank, Copy,
} from "@phosphor-icons/react";

const Stat = ({ label, value, sub, i = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: i * 0.05 }}
    className="glass rounded-xl p-5"
    data-testid={`trust-stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`}
  >
    <div className="eyebrow text-[10px]">{label}</div>
    <div className="mono text-3xl mt-3">{value}</div>
    {sub && <div className="text-[11px] text-white/50 mt-1">{sub}</div>}
  </motion.div>
);

const GdprPill = ({ label, active, i }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.05 }}
    className={`glass rounded-full px-4 py-2 flex items-center gap-2 text-xs ${active ? "border-[#00A86B]/40" : ""}`}
    data-testid={`gdpr-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
  >
    <CheckCircle size={14} weight="fill" color="#00A86B" />
    <span className="text-white/85">{label}</span>
  </motion.div>
);

export default function TrustCenter() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState({ loading: true, error: "" });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/trust/${slug}`);
        if (!alive) return;
        setData(r.data);
        setState({ loading: false, error: "" });
        // SEO: dynamic title + description
        document.title = `Trust Center · ${r.data.org_name} · PerCen360`;
        const m = document.querySelector('meta[name="description"]') || (() => {
          const el = document.createElement("meta"); el.name = "description"; document.head.appendChild(el); return el;
        })();
        m.content = `${r.data.org_name} — live compliance score ${r.data.stats.compliance_score}%, ${r.data.stats.grants_tracked} grants tracked, GDPR-native. Verified by PerCen360.`;
      } catch (e) {
        setState({
          loading: false,
          error: e?.response?.status === 404 ? "Trust page not found" : "Unable to load trust page",
        });
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.warn("[TrustCenter] clipboard copy failed:", err?.message || err);
    }
  };

  if (state.loading) return (
    <div className="min-h-screen aurora flex items-center justify-center">
      <div className="glass rounded-2xl px-10 py-8 text-center">
        <div className="eyebrow mb-3">Loading Trust Center…</div>
        <span className="dot-flash"/><span className="dot-flash"/><span className="dot-flash"/>
      </div>
    </div>
  );

  if (state.error) return (
    <div className="min-h-screen aurora flex items-center justify-center px-6">
      <div className="glass rounded-2xl px-10 py-10 text-center max-w-md">
        <XCircle size={40} weight="duotone" color="#E63946" className="mx-auto mb-4"/>
        <div className="font-display text-2xl mb-2">Trust page not available</div>
        <div className="text-white/60 text-sm">{state.error}</div>
        <Link to="/" className="btn-ghost mt-6 inline-flex" data-testid="trust-back-home">← Back to PerCen360</Link>
      </div>
    </div>
  );

  const s = data.stats;
  const activeErpSet = new Set(data.active_erp || []);
  const certUrl = data.active_certificate
    ? `/certificate/${data.active_certificate.share_token}`
    : null;

  return (
    <div className="min-h-screen aurora grain" data-testid="trust-page">
      {/* Top bar */}
      <div className="border-b border-white/5 bg-black/40 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="trust-logo-home">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "#FFB020" }}>
              <ShieldCheck size={22} weight="duotone" color="#050B0A" />
            </div>
            <div>
              <div className="font-display leading-none">PerCen360</div>
              <div className="text-[9px] tracking-[0.24em] uppercase text-white/45 mt-1">Trust Center</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={copyLink} className="btn-ghost text-xs" data-testid="trust-copy-link">
              <Copy size={13} weight="bold" /> {copied ? "Copied" : "Copy link"}
            </button>
            {certUrl && (
              <Link to={certUrl} className="btn-gold text-xs" data-testid="trust-view-cert">
                <CertIcon size={13} weight="fill" /> Live certificate
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 sm:px-10 py-14 space-y-14">
        {/* Hero */}
        <section className="grid lg:grid-cols-3 gap-8 items-center">
          <div className="lg:col-span-2 space-y-4">
            <div className="eyebrow flex items-center gap-2">
              <MapPin size={12} weight="fill" color="#7AE1B3" /> {data.gdpr.region}
              <span className="text-white/30">·</span>
              <span>Live · updated in real time</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              {data.org_name} is <span style={{ color: "#FFB020" }}>compliance-verified</span> by PerCen360.
            </h1>
            <p className="text-white/70 text-base leading-relaxed max-w-2xl">
              GDPR-native, Revenue.ie rule-checked, ERP-portable. Every figure below is derived from live data and
              stamped with a tamper-evident SHA-256 audit hash. No PII is exposed on this page.
            </p>
            <div className="flex items-center gap-3 flex-wrap pt-2">
              {certUrl && (
                <Link to={certUrl} className="btn-gold" data-testid="trust-hero-cta-cert">
                  <CertIcon size={14} weight="fill" /> Verify the certificate
                </Link>
              )}
              <a href="#stats" className="btn-ghost">See the numbers</a>
              <a href="#gdpr" className="btn-ghost">GDPR posture</a>
            </div>
          </div>

          {/* Compliance score dial */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.1 }}
            className="glass rounded-2xl p-8 text-center relative"
            data-testid="trust-score-dial"
          >
            <div className="eyebrow mb-3">Compliance score</div>
            <div className="relative w-48 h-48 mx-auto">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle cx="50" cy="50" r="42" stroke="rgba(255,255,255,0.08)" strokeWidth="8" fill="none" />
                <circle
                  cx="50" cy="50" r="42"
                  stroke="#FFB020" strokeWidth="8" fill="none"
                  strokeLinecap="round"
                  strokeDasharray={`${(s.compliance_score / 100) * (2 * Math.PI * 42)} ${2 * Math.PI * 42}`}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="mono text-5xl" style={{ color: "#FFB020" }}>{s.compliance_score}<span className="text-2xl">%</span></div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">of {s.receipts_captured} receipts pass</div>
              </div>
            </div>
            <div className="mt-6 text-xs text-white/60">
              Verified {data.verified_at?.slice(0, 10)} · updated live
            </div>
          </motion.div>
        </section>

        {/* Live stats */}
        <section id="stats" className="space-y-6">
          <div>
            <div className="eyebrow">Live compliance snapshot</div>
            <h2 className="font-display text-3xl mt-1">The numbers, straight from the source.</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <Stat i={0} label="Grants tracked" value={s.grants_tracked} sub="active + closed" />
            <Stat i={1} label="Awarded" value={`€${(s.total_awarded).toLocaleString()}`} sub="lifetime funding" />
            <Stat i={2} label="Utilisation" value={`${s.utilisation_pct}%`} sub={`€${s.total_spent.toLocaleString()} spent`} />
            <Stat i={3} label="Receipts captured" value={s.receipts_captured} sub="Revenue.ie rule-checked" />
            <Stat i={4} label="Certificates" value={s.certificates_issued} sub={`${s.cert_views} public views`} />
            <Stat i={5} label="ERP exports (90d)" value={s.csv_exports_90d} sub="audit-portable" />
          </div>
        </section>

        {/* Active certificate */}
        {data.active_certificate && (
          <section className="glass rounded-2xl p-8 relative overflow-hidden" data-testid="trust-active-cert">
            <div className="absolute inset-0 bg-gradient-to-br from-[#FFB020]/8 via-transparent to-transparent pointer-events-none" />
            <div className="relative grid md:grid-cols-3 gap-6 items-center">
              <div className="md:col-span-2 space-y-2">
                <div className="eyebrow flex items-center gap-2">
                  <CertIcon size={12} weight="fill" color="#FFB020" /> Active Compliance Certificate
                </div>
                <div className="font-display text-2xl">
                  Score {data.active_certificate.compliance_score}% · valid until {data.active_certificate.expires_at?.slice(0, 10)}
                </div>
                <div className="text-white/60 text-sm flex items-center gap-2 flex-wrap">
                  <Hash size={12} /> <span className="mono text-xs">{data.active_certificate.audit_hash}</span>
                  <span className="text-white/30">·</span>
                  <CalendarBlank size={12} /> Issued {data.active_certificate.issued_at?.slice(0, 10)}
                </div>
              </div>
              <div className="flex md:justify-end">
                <Link to={certUrl} className="btn-gold" data-testid="trust-open-cert">
                  <ArrowSquareOut size={14} weight="fill" /> Open certificate
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* ERP compatibility */}
        <section className="space-y-6">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="eyebrow flex items-center gap-2">
                <PlugsConnected size={12} weight="fill" color="#FFB020" /> ERP portability
              </div>
              <h2 className="font-display text-3xl mt-1">Data leaves in every format an auditor expects.</h2>
            </div>
            <div className="text-xs text-white/50">
              <span className="mono">{data.active_erp?.length || 0}</span> of {data.erp_platforms.length} platforms already exported
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="trust-erp-grid">
            {data.erp_platforms.map((p) => {
              const active = activeErpSet.has(p.slug);
              return (
                <div
                  key={p.slug}
                  className={`glass rounded-xl px-4 py-3 flex items-center justify-between ${active ? "border-[#00A86B]/40" : ""}`}
                  data-testid={`trust-erp-${p.slug}`}
                >
                  <span className="text-sm text-white/85">{p.label}</span>
                  {active && <CheckCircle size={14} weight="fill" color="#00A86B" />}
                </div>
              );
            })}
          </div>
        </section>

        {/* GDPR posture */}
        <section id="gdpr" className="glass rounded-2xl p-8 space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#00A86B]/10 border border-[#00A86B]/40">
              <LockKey size={22} weight="duotone" color="#00A86B" />
            </div>
            <div>
              <div className="eyebrow">GDPR & security posture</div>
              <div className="font-display text-2xl">EU-hosted, minimally-collected, audit-logged.</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <GdprPill label="Art. 5 · Data minimisation" active={data.gdpr.art_5_data_minimisation} i={0} />
            <GdprPill label="Art. 30 · Processing records" active={data.gdpr.art_30_processing_records} i={1} />
            <GdprPill label="Art. 32 · Encryption in transit & at rest" active={data.gdpr.art_32_encryption} i={2} />
            <GdprPill label={data.gdpr.region} active i={3} />
          </div>
          <div className="pt-4 border-t border-white/5 flex flex-wrap gap-4 items-center">
            <Link to={data.gdpr.dpia_url} className="btn-ghost text-xs" data-testid="trust-dpia-link">
              <FileText size={13} weight="fill" /> Read the DPIA
            </Link>
            <Link to="/privacy" className="btn-ghost text-xs">Privacy policy</Link>
            <Link to="/terms" className="btn-ghost text-xs">Terms</Link>
            <span className="text-xs text-white/45 ml-auto">
              {data.verified_by}
            </span>
          </div>
        </section>

        {/* Footer CTA */}
        <section className="text-center py-10">
          <div className="eyebrow mb-2">Powered by PerCen360</div>
          <div className="font-display text-2xl mb-3">Get your own live Trust page.</div>
          <p className="text-white/60 text-sm max-w-lg mx-auto">
            One URL for grant applications, investors and auditors — auto-generated from your live compliance data.
          </p>
          <Link to="/" className="btn-gold mt-6 inline-flex" data-testid="trust-cta-signup">
            Start compliance-guardianing →
          </Link>
        </section>
      </div>

      <footer className="border-t border-white/5 py-6">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-10 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-white/45">
          <div>percen360.com · EU region · GDPR compliant</div>
          <div className="flex gap-5">
            <Link to="/privacy" className="hover:text-white/80">Privacy</Link>
            <Link to="/terms" className="hover:text-white/80">Terms</Link>
            <Link to="/dpia" className="hover:text-white/80">DPIA</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
